import { PairedCostAdjustmentType } from '@aloft/toolkit-shared';
import { ProductColor } from '@aloft/toolkit-sui';

import { DetailFeatures } from '../../../../redux/constants.ts';
import { selectFeatureDetailsByIDAndFeature } from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { RootState } from '../../../../redux/types.ts';
import { DetailLineWithValue } from '../../ColumnComponents/DetailLineWithValue.tsx';
import { PLACEHOLDER } from '../../consts.ts';
import { ViewMode } from '../../types.ts';
import { FEATURE_ADJUSTMENT_SELECTORS } from './featureAdjustmentSelectors.ts';
import { FEATURE_COST_SELECTORS } from './featureCostSelectors.ts';

const EMPTY_ADJUSTMENT: { value: string; color: ProductColor } = {
  value: '',
  color: 'textTertiary',
};

type AdjustmentDetailProps = {
  id: string;
  feature: DetailFeatures;
  viewMode: Extract<ViewMode, 'adjustments'>;
  group: PairedCostAdjustmentType;
  firstInGroup: boolean;
  lastInGroup: boolean;
};

type FeatureDetailProps =
  | {
      id: string;
      feature: DetailFeatures;
      viewMode: Exclude<ViewMode, 'adjustments'>;
    }
  | AdjustmentDetailProps;

function AdjustmentFeatureDetail({
  id,
  feature,
  group,
  firstInGroup,
  lastInGroup,
}: Omit<AdjustmentDetailProps, 'viewMode'>) {
  const details = useAppSelector((state: RootState) =>
    selectFeatureDetailsByIDAndFeature(state, id, feature),
  );

  const adjSelector =
    (lastInGroup ? FEATURE_ADJUSTMENT_SELECTORS[group] : undefined) ??
    (() => {
      return EMPTY_ADJUSTMENT;
    });
  const adj = useAppSelector((state: RootState) => adjSelector(state, id));

  return (
    <DetailLineWithValue
      text={details}
      value={adj?.value}
      valueColor={adj?.color}
      topBorder={firstInGroup}
    />
  );
}

function CostFeatureDetail({ id, feature }: Omit<FeatureDetailProps, 'viewMode' | 'group'>) {
  const details = useAppSelector((state: RootState) =>
    selectFeatureDetailsByIDAndFeature(state, id, feature),
  );

  const costSelector = FEATURE_COST_SELECTORS[feature] ?? (() => PLACEHOLDER);
  const costs = useAppSelector((state: RootState) => costSelector(state, id));

  return <DetailLineWithValue text={details} value={costs} />;
}

export function FeatureDetail(props: FeatureDetailProps) {
  if (props.viewMode === 'adjustments') {
    return <AdjustmentFeatureDetail {...props} />;
  }

  return <CostFeatureDetail {...props} />;
}
